import { useState, useCallback } from 'react'
import { Modal, Input, Table, Tag, Space, Empty, Spin, Typography } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import type { ColumnsType } from 'antd/es/table'
import axios from '@/utils/axios'
import debounce from 'lodash/debounce'

const { Text } = Typography

interface SearchResult {
  id: number
  order_number: string
  delivery_number: string
  recipient_name: string
  recipient_phone: string
  buyer_name: string
  site_name: string
  customs_number: string
  order_date: string
  picking_status: string
  order_type: string
  tab_name: string
  tab_key: string
  item_count: number
  product_names: string[]
}

interface GlobalSearchModalProps {
  open: boolean
  onClose: () => void
}

// 탭별 색상 매핑
const tabColorMap: Record<string, string> = {
  '주문서': 'blue',
  '배송대기': 'cyan',
  '검수완료': 'orange',
  '배송완료': 'green',
  '선포장': 'purple',
  '미등록': 'red',
  '구매곤란': 'volcano',
  '취소': 'default',
  '분할배송': 'geekblue',
  '3PL': 'lime',
  '통관검증': 'gold',
  '휴지통': 'default',
}

// 상태별 색상 매핑
const statusColorMap: Record<string, string> = {
  '대기': 'default',
  '피킹중': 'processing',
  '피킹완료': 'cyan',
  '검수완료': 'orange',
  '배송완료': 'green',
  '취소': 'default',
  '선포장': 'purple',
  '구매곤란': 'volcano',
  '휴지통': 'default',
  '보류': 'warning',
}

// 타입별 색상 매핑
const typeColorMap: Record<string, string> = {
  '일반': 'blue',
  '합포': 'purple',
  '선물': 'magenta',
  '교환': 'orange',
  '반품': 'red',
}

// 탭별 경로 매핑
const tabPathMap: Record<string, string> = {
  'orders': '/orders',
  'pending': '/orders/pending',
  'inspected': '/orders/inspected',
  'completed': '/orders/completed',
  'prepacked': '/orders/prepacked',
  'unregistered': '/orders/unregistered',
  'problem': '/orders/problem',
  'cancelled': '/orders/cancelled',
  'split_delivery': '/orders/split-delivery',
  '3pl': '/orders/3pl',
  'verification': '/orders/verification',
  'trash': '/orders/trash',
}

export default function GlobalSearchModal({ open, onClose }: GlobalSearchModalProps) {
  const navigate = useNavigate()
  const [searchText, setSearchText] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<SearchResult[]>([])
  const [searched, setSearched] = useState(false)

  // 검색 실행 (debounce 적용)
  const doSearch = useCallback(
    debounce(async (query: string) => {
      if (!query || query.length < 2) {
        setResults([])
        setSearched(false)
        return
      }

      setLoading(true)
      setSearched(true)

      try {
        const response = await axios.get('/api/global-search/', {
          params: { q: query, limit: 50 }
        })

        if (response.data.success) {
          setResults(response.data.results)
        } else {
          setResults([])
        }
      } catch (error) {
        console.error('검색 오류:', error)
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 300),
    []
  )

  // 검색어 변경 시
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchText(value)
    doSearch(value)
  }

  // 행 클릭 시 해당 탭으로 이동
  const handleRowClick = (record: SearchResult) => {
    const path = tabPathMap[record.tab_key] || '/orders'
    // 주문 상세 페이지로 이동
    navigate(`/orders/${record.order_number}`)
    onClose()
    setSearchText('')
    setResults([])
    setSearched(false)
  }

  // 탭으로 바로 이동
  const handleGoToTab = (record: SearchResult, e: React.MouseEvent) => {
    e.stopPropagation()
    const path = tabPathMap[record.tab_key] || '/orders'
    navigate(path)
    onClose()
    setSearchText('')
    setResults([])
    setSearched(false)
  }

  // 모달 닫을 때 초기화
  const handleClose = () => {
    setSearchText('')
    setResults([])
    setSearched(false)
    onClose()
  }

  // 테이블 컬럼
  const columns: ColumnsType<SearchResult> = [
    {
      title: '위치',
      dataIndex: 'tab_name',
      key: 'tab_name',
      width: 90,
      render: (tabName: string, record: SearchResult) => (
        <Tag
          color={tabColorMap[tabName] || 'default'}
          style={{ cursor: 'pointer' }}
          onClick={(e) => handleGoToTab(record, e)}
        >
          {tabName}
        </Tag>
      ),
    },
    {
      title: '상태',
      dataIndex: 'picking_status',
      key: 'picking_status',
      width: 80,
      render: (status: string) => (
        <Tag color={statusColorMap[status] || 'default'}>{status}</Tag>
      ),
    },
    {
      title: '타입',
      dataIndex: 'order_type',
      key: 'order_type',
      width: 60,
      render: (type: string) => type ? (
        <Tag color={typeColorMap[type] || 'default'}>{type}</Tag>
      ) : '-',
    },
    {
      title: '사이트',
      dataIndex: 'site_name',
      key: 'site_name',
      width: 90,
    },
    {
      title: '주문번호',
      dataIndex: 'order_number',
      key: 'order_number',
      width: 140,
      ellipsis: true,
    },
    {
      title: '송장번호',
      dataIndex: 'delivery_number',
      key: 'delivery_number',
      width: 120,
      render: (text: string) => text || '-',
    },
    {
      title: '수취인',
      dataIndex: 'recipient_name',
      key: 'recipient_name',
      width: 70,
    },
    {
      title: '통관번호',
      dataIndex: 'customs_number',
      key: 'customs_number',
      width: 130,
      render: (text: string) => text || '-',
    },
    {
      title: '전화번호',
      dataIndex: 'recipient_phone',
      key: 'recipient_phone',
      width: 110,
      render: (text: string) => text || '-',
    },
    {
      title: '품목수',
      dataIndex: 'item_count',
      key: 'item_count',
      width: 70,
      align: 'center',
      render: (count: number) => count ? <Tag color="blue">{count}</Tag> : '-',
    },
  ]

  return (
    <Modal
      title={
        <Space>
          <SearchOutlined />
          <span>주문 통합 검색</span>
        </Space>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={1400}
      styles={{ body: { padding: '16px 24px' } }}
    >
      <Input
        placeholder="주문번호, 송장번호, 수취인명, 구매자명, 통관번호, 사이트명으로 검색..."
        prefix={<SearchOutlined />}
        value={searchText}
        onChange={handleSearchChange}
        size="large"
        allowClear
        autoFocus
        style={{ marginBottom: 16 }}
      />

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: '#999' }}>검색 중...</div>
        </div>
      ) : searched && results.length === 0 ? (
        <Empty
          description={
            <span>
              "<strong>{searchText}</strong>" 검색 결과가 없습니다
            </span>
          }
          style={{ padding: 40 }}
        />
      ) : results.length > 0 ? (
        <>
          <div style={{ marginBottom: 8, color: '#666' }}>
            검색 결과: <strong>{results.length}</strong>건
            <Text type="secondary" style={{ marginLeft: 16, fontSize: 12 }}>
              행을 클릭하면 상세 페이지로 이동합니다
            </Text>
          </div>
          <Table
            columns={columns}
            dataSource={results}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: false }}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: 'pointer' }
            })}
            scroll={{ y: 400 }}
          />
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          <SearchOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
          <div>주문번호, 송장번호, 수취인명, 구매자명, 통관번호, 사이트명으로 검색해보세요</div>
          <div style={{ fontSize: 12, marginTop: 8 }}>
            모든 탭(주문서, 배송대기, 검수완료 등)에서 통합 검색됩니다
          </div>
        </div>
      )}
    </Modal>
  )
}
